import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Column } from "@/components/tables/DataTable";
import { Bell, ChevronRight, Plus, RotateCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { useGetAdminNotificationsQuery, type Notification } from "@/redux/slices/notificationApi";

export default function Notifications() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const { data: response, isFetching, refetch } = useGetAdminNotificationsQuery({
    page,
    limit,
    search: "",
  });

  const notifications = response?.data || [];
  const total = response?.total || 0;

  const columns: Column<Notification>[] = [
    {
      key: "title",
      header: "Title",
      render: (r) => (
        <div className="font-medium text-foreground">{r.title}</div>
      ),
    },
    {
      key: "body",
      header: "Message",
      render: (r) => (
        <div className="max-w-md truncate text-muted-foreground" title={r.body}>
          {r.body}
        </div>
      ),
    },
    {
      key: "target",
      header: "Target",
      render: (r) => (
        r.target === "all" ? (
          <Badge variant="secondary" className="bg-primary/10 text-primary">Broadcast</Badge>
        ) : (
          <Badge variant="outline">Selected Students</Badge>
        )
      ),
    },
    {
      key: "createdAt",
      header: "Date",
      render: (r) => format(new Date(r.createdAt), "dd MMM yyyy, hh:mm a"),
    },
  ];

  return (
    <div className="min-h-screen bg-muted/20">
      <PageHeader
        title="Push Notifications"
        description="View and manage all push notifications sent to your students."
        action={
          <div className="flex items-center space-x-3">
            <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching}>
              <RotateCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
            </Button>
            <Button onClick={() => navigate('/notifications/send')}>
              <Plus className="mr-2 h-4 w-4" />
              Send Notification
            </Button>
          </div>
        }
      />
      <div className="space-y-6 p-4 sm:p-6 lg:p-8">
        <DataTable
          columns={columns}
          rows={notifications}
          loading={isFetching}
          total={total}
          page={page}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
          emptyText="No notifications found"
          actions={(row) => (
            <Button variant="ghost" size="sm" asChild>
              <Link to={`/notifications/${row.id || (row as any)._id}`}>
                Details
                <ChevronRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          )}
        />
      </div>
    </div>
  );
}
